import { z } from 'zod';
import { appId } from '../../contracts/app-v2-primitives.mjs';
import { canonicalJson } from '../../data-formats/canonical-json.mjs';

export const SERIES_JOB_INPUT_VERSION = 'app-series-job.v1';

const inputSchema = z
  .object({
    schemaVersion: z.literal(SERIES_JOB_INPUT_VERSION),
    kind: z.enum(['app_refresh', 'app_backfill']),
    seriesId: appId,
    keyFieldIds: z.array(appId).min(1).max(100),
    pageSize: z.number().int().min(1).max(128),
    maxPages: z.number().int().min(1).max(100),
    input: z.json(),
  })
  .strict()
  .refine((input) => new Set(input.keyFieldIds).size === input.keyFieldIds.length, {
    path: ['keyFieldIds'],
    message: 'Use unique declared key columns',
  });

/** Strict authored settings, not function input or an execution grant. */
export function parseSavedSeriesJobInput(value) {
  const input = inputSchema.parse(value);
  return JSON.parse(canonicalJson(input, { maxBytes: 65536 }));
}

/** The exact reserved tag cannot fall through as an ordinary job. */
export function readSavedSeriesJobInput(value) {
  return value?.schemaVersion === SERIES_JOB_INPUT_VERSION ? parseSavedSeriesJobInput(value) : null;
}

export function validateSavedSeriesJobReferences(manifest, ctx) {
  const pairs = new Set(),
    seriesIds = new Set(manifest.series.map((series) => series.id));
  manifest.jobs.forEach((job, index) => {
    try {
      const input = readSavedSeriesJobInput(job.input);
      if (!input) return;
      const pair = JSON.stringify([input.kind, job.functionId]);
      if (pairs.has(pair) || !seriesIds.has(input.seriesId))
        throw new TypeError('Use one declared series job per kind and function');
      pairs.add(pair);
    } catch {
      ctx.addIssue({
        code: 'custom',
        path: ['jobs', index, 'input'],
        message: 'Invalid or ambiguous saved series job',
      });
    }
  });
}
